import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import Qs from 'qs';
import { appLogout } from 'utils/session';
import { LOCAL_STORAGE_KEYS } from 'utils/constants';
import { CsrfStorage } from './csrf';

const queryAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_BASE_URL,
  timeout: 120000,
  withCredentials: true,
  responseType: 'json',
  responseEncoding: 'utf8',

  // disable unexpected cache behaviour
  // Solution: https://stackoverflow.com/a/62781874
  headers: {
    ...(process.env.REACT_APP_BACKEND_REFERRER
      ? {
          Referrer: process.env.REACT_APP_BACKEND_REFERRER || '',
        }
      : {}),
    'Cache-Control': 'no-store',
    Pragma: 'no-cache',
    Expires: 0,
  },
});

const mutateAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_BASE_URL,
  timeout: 15000,
  withCredentials: true,
  responseType: 'json',
  responseEncoding: 'utf8',
  headers: {
    ...(process.env.REACT_APP_BACKEND_REFERRER
      ? {
          Referrer: process.env.REACT_APP_BACKEND_REFERRER || '',
        }
      : {}),
  },
});

const checkFirewalledResponse = function (response: AxiosResponse) {
  // check for redirect responses with header "X-Firewalled: true" or HTTP status 430 with JSON key firewall
  const isFirewalled =
    response.headers['x-firewalled'] === 'true' || (430 === response.status && response.data?.firewall);

  if (isFirewalled) {
    const location = response.headers['location'] || response.data.location || 'https://www.smoove.world';
    console.warn('Backend is currently firewalling requests from this IP address');

    window.location.href = location;
  }

  return response;
};

/**
 * Response interceptor that captures CSRF tokens from every response.
 *
 * Updates the plaintext token from the X-CSRF-TOKEN response header
 * (used for cross-domain deployments) and the body-meta CSRF object.
 * This is critical because login regenerates the session (and thus
 * the CSRF token), so it must be captured from every response — not
 * just /sanctum/csrf-cookie.
 */
const saveCsrfOnEachResponse = function (response: AxiosResponse) {
  // capture plaintext CSRF token from the response header (cross-domain)
  const headerToken = response.headers?.['x-csrf-token'];
  if (headerToken) {
    CsrfStorage.xsrfToken = headerToken;
  }

  // capture CSRF object from response body meta (legacy)
  if (typeof response === 'object') {
    const csrf = response?.data?.meta?.csrf || response?.data?.meta?.meta?.csrf;
    if (typeof csrf === 'object' && 'name' in csrf && 'value' in csrf) {
      CsrfStorage.saved = csrf;
    }
  }

  return response;
};

/**
 * Request interceptor that attaches authentication and CSRF headers.
 *
 * Adds Bearer token, X-CSRF-TOKEN or X-XSRF-TOKEN (depending on token
 * source), device push token, and cache-busting params.
 */
const injectAuthHeader = function (config: InternalAxiosRequestConfig) {
  const access_token = localStorage.getItem('access_token');

  if (access_token) {
    try {
      config.headers.Authorization = `Bearer ${access_token}`;
    } catch (err) {
      console.error(err);
    }
  }

  // attach the CSRF/XSRF token: when a plaintext token was obtained from the
  // X-CSRF-TOKEN response header (cross-domain), send it back as X-CSRF-TOKEN;
  // otherwise fall back to the encrypted cookie which Axios sends as X-XSRF-TOKEN
  try {
    const csrfHeader = CsrfStorage.getCsrfHeader();

    if (csrfHeader) {
      config.headers[csrfHeader.name] = csrfHeader.value;
    } else {
      const xsrfToken = Cookies.get('XSRF-TOKEN');

      if (xsrfToken) {
        config.headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }
  } catch (err) {
    console.error(err);
  }

  const deviceToken = localStorage.getItem(LOCAL_STORAGE_KEYS.devicePushToken);
  if (deviceToken) {
    config.headers['Device-Apn'] = deviceToken;
  }

  config.paramsSerializer = (params) => {
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: true,
    });
  };

  config.params = { ...config.params, dt: Date.now() + Math.random().toString(10) };

  return config;
};

const handleError = function (error: AxiosError) {
  if (error.response) {
    checkFirewalledResponse(error.response);
  }

  // Do something with request error
  if (error.response?.status === 401) {
    appLogout();
  }
  return Promise.reject(error);
};

queryAxiosInstance.interceptors.request.use(injectAuthHeader, handleError);
queryAxiosInstance.interceptors.response.use(saveCsrfOnEachResponse, handleError);
mutateAxiosInstance.interceptors.request.use(injectAuthHeader, handleError);
mutateAxiosInstance.interceptors.response.use(saveCsrfOnEachResponse, handleError);

const axiosClient = {
  query: queryAxiosInstance,
  mutate: mutateAxiosInstance,
};

export default axiosClient;
